import { api } from '../../../../../config';
import { getAuthToken } from '../../../../../auth/tokenvalidation';

const isFilled = value =>
  value !== null &&
  value !== undefined &&
  value !== '-' &&
  String(value).trim() !== '';

const pickNamedValue = value => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'object') {
    return (
      value.name ??
      value.Name ??
      value.location?.name ??
      value.location?.Name ??
      value.Location?.name ??
      value.Location?.Name ??
      null
    );
  }
  return value;
};

const displayCell = value => {
  const resolved = pickNamedValue(value) ?? value;
  if (!isFilled(resolved)) return '-';
  return String(resolved).trim();
};

const isFobIncoterm = incoterm =>
  String(incoterm || '')
    .trim()
    .toUpperCase() === 'FOB';

const getFobLocationNames = data =>
  (Array.isArray(data?.incotermFOBLocations)
    ? data.incotermFOBLocations
    : Array.isArray(data?.IncotermFOBLocations)
      ? data.IncotermFOBLocations
      : []
  )
    .map(location => location?.name ?? location?.Name ?? location)
    .filter(isFilled);

const isFobOriginRoute = (route, names = []) => {
  const routeText = String(route || '')
    .trim()
    .toLowerCase();
  if (routeText === 'origin') return true;
  if (routeText === 'straits') return false;
  return (Array.isArray(names) ? names : []).filter(isFilled).length > 2;
};

const resolveRoute = (row, timelineItem) => {
  const candidates = [
    row?.route,
    timelineItem?.incotermRoute,
    timelineItem?.incotermPortType,
    row?.incotermPortType,
  ];
  for (const candidate of candidates) {
    const resolved = displayCell(candidate);
    if (resolved !== '-') return resolved;
  }
  return '-';
};

const getFobLocationObjects = data =>
  (Array.isArray(data?.incotermFOBLocations)
    ? data.incotermFOBLocations
    : Array.isArray(data?.IncotermFOBLocations)
      ? data.IncotermFOBLocations
      : []
  ).filter(location => location && typeof location === 'object');

const collectFobLocations = (row, timelineItem) => {
  const fromRow = getFobLocationObjects(row);
  if (fromRow.length) return fromRow;
  return getFobLocationObjects(timelineItem);
};

const collectFobLocationNames = (row, timelineItem) => {
  const rowLocations = getFobLocationObjects(row);
  if (rowLocations.length) {
    const fromRowObjects = getFobLocationNames(row);
    if (fromRowObjects.length) return fromRowObjects;
  }

  const fromTimeline = getFobLocationNames(timelineItem);
  if (fromTimeline.length) return fromTimeline;

  const fromRow = getFobLocationNames(row);
  if (fromRow.length) return fromRow;

  return [
    timelineItem?.incotermLocation,
    timelineItem?.incotermLocation2,
    row?.loadingPort,
    row?.port1,
  ]
    .map(value => displayCell(value))
    .filter(value => value !== '-');
};

const pickFirstFilled = (...values) => {
  for (const value of values) {
    if (value !== null && value !== undefined && value !== '') return value;
  }
  return null;
};

const readAlphaDyed = source =>
  pickFirstFilled(
    source?.alphaDyedHps,
    source?.AlphaDyedHps,
    source?.hpsAlphaDyed,
    source?.HpsAlphaDyed,
    source?.alphaHpsDyed,
    source?.hpsDyed
  );

const readAlphaUndyed = source =>
  pickFirstFilled(
    source?.alphaUndyedHps,
    source?.AlphaUndyedHps,
    source?.hpsAlphaUndyed,
    source?.HpsAlphaUndyed,
    source?.alphaHpsUndyed,
    source?.hpsUndyed
  );

const readAlphaHps = source =>
  pickFirstFilled(
    source?.alphaHps,
    source?.AlphaHps,
    source?.hpsAlpha,
    source?.HpsAlpha
  );

const resolveFobLoadingPort = (route, names, fallback) => {
  if (!names.length) return displayCell(fallback);
  if (isFobOriginRoute(route, names)) return names[0];
  return names.join(' or ');
};

const findTimelineItem = (row, timelineItems, index) => {
  if (!timelineItems.length) return null;

  if (row?.quantityIncotermId != null) {
    const matched = timelineItems.find(
      item =>
        item?.quantityIncotermId != null &&
        String(item.quantityIncotermId) === String(row.quantityIncotermId)
    );
    if (matched) return matched;
  }

  return timelineItems[index] ?? null;
};

const mapDisplayRow = (row, timelineItem) => {
  const incoterm = displayCell(row?.incoterm ?? timelineItem?.incoterm);
  const route = resolveRoute(row, timelineItem);
  const fobLocationNames = collectFobLocationNames(row, timelineItem);

  const loadingPort = isFobIncoterm(incoterm)
    ? resolveFobLoadingPort(route, fobLocationNames, row?.loadingPort)
    : '-';

  return {
    ...row,
    incoterm,
    route,
    loadingPort,
    fobLocationNames,
    fobLocations: collectFobLocations(row, timelineItem),
    alphaDyedHps: readAlphaDyed(row),
    alphaUndyedHps: readAlphaUndyed(row),
    alphaHps: readAlphaHps(row),
  };
};

const expandFobRows = rows =>
  rows.flatMap(row => {
    if (!isFobIncoterm(row.incoterm)) {
      return [{ ...row, loadingPort: '-' }];
    }

    const locations = (row.fobLocations ?? []).filter(
      location => location && typeof location === 'object'
    );

    // Samakan module HPS ModelDeliveryFragment: setiap FOB + lokasi = 1 baris
    // (Origin maupun Straits) supaya alpha per lokasi tidak hilang.
    if (locations.length) {
      return locations
        .map(location => {
          const name = location?.name ?? location?.Name;
          if (!isFilled(name)) return null;
          return {
            ...row,
            loadingPort: name,
            route: pickFirstFilled(location?.route, location?.Route, row.route),
            deliveryBasis: pickFirstFilled(
              location?.deliveryBasis,
              location?.DeliveryBasis,
              row.deliveryBasis
            ),
            alphaDyedHps: readAlphaDyed(location) ?? readAlphaDyed(row),
            alphaUndyedHps: readAlphaUndyed(location) ?? readAlphaUndyed(row),
            alphaHps: readAlphaHps(location) ?? readAlphaHps(row),
          };
        })
        .filter(Boolean);
    }

    return [row];
  });

export const mapHpsParcelIncotermRows = parcel => {
  const rows = Array.isArray(parcel?.incotermRows) ? parcel.incotermRows : [];
  const timelineItems = Array.isArray(parcel?.incotermTimelineItems)
    ? parcel.incotermTimelineItems
    : [];

  if (!rows.length && timelineItems.length) {
    return expandFobRows(timelineItems.map(item => mapDisplayRow({}, item)));
  }

  const mapped = rows.map((row, index) =>
    mapDisplayRow(row, findTimelineItem(row, timelineItems, index))
  );

  return expandFobRows(mapped);
};

const toTableRowFromDetail = (details, location = null) => {
  const source = location || details;
  const isFob = isFobIncoterm(details?.incoterm ?? details?.Incoterm);
  const qty1 = pickFirstFilled(
    source?.parcelSize,
    source?.ParcelSize,
    details?.parcelSize,
    details?.ParcelSize,
    details?.deliveryBasis,
    details?.DeliveryBasis
  );

  return {
    deliveryBasis: pickFirstFilled(
      source?.deliveryBasis,
      source?.DeliveryBasis,
      details?.deliveryBasis,
      details?.DeliveryBasis
    ),
    incoterm: pickFirstFilled(details?.incoterm, details?.Incoterm),
    route: isFob
      ? pickFirstFilled(
          source?.route,
          source?.Route,
          details?.route,
          details?.Route
        )
      : '',
    loadingPort: isFob
      ? pickFirstFilled(source?.name, source?.Name, source?.loadingPort)
      : '-',
    dischargeQty1: isFob && location ? null : qty1,
    port1:
      isFob && location
        ? null
        : pickFirstFilled(details?.port1, details?.Port1),
    aldAdd1: isFob
      ? pickFirstFilled(source?.addAld, source?.AddAld, details?.addAld)
      : pickFirstFilled(details?.addAld, details?.AddAld),
    dischargeQty2:
      isFob && location
        ? null
        : pickFirstFilled(details?.parcelSize2, details?.ParcelSize2),
    port2:
      isFob && location
        ? null
        : pickFirstFilled(details?.port2, details?.Port2),
    aldAdd2:
      isFob && location
        ? null
        : pickFirstFilled(details?.addAld2, details?.AddAld2),
    dischargeQty3:
      isFob && location
        ? null
        : pickFirstFilled(details?.parcelSize3, details?.ParcelSize3),
    port3:
      isFob && location
        ? null
        : pickFirstFilled(details?.port3, details?.Port3),
    aldAdd3:
      isFob && location
        ? null
        : pickFirstFilled(details?.addAld3, details?.AddAld3),
    alphaDyedHps: readAlphaDyed(source) ?? readAlphaDyed(details),
    alphaUndyedHps: readAlphaUndyed(source) ?? readAlphaUndyed(details),
    alphaHps: readAlphaHps(source) ?? readAlphaHps(details),
    quantityIncotermId: pickFirstFilled(
      details?.quantityIncotermId,
      details?.QuantityIncotermId
    ),
  };
};

const buildRowsFromDeliveryDetailResult = result => {
  const groups = Array.isArray(result?.data?.data)
    ? result.data.data
    : Array.isArray(result?.data?.Data)
      ? result.data.Data
      : Array.isArray(result?.data)
        ? result.data
        : [];

  return groups.flatMap(item => {
    const listData = item?.listData || item?.ListData || [];
    return (Array.isArray(listData) ? listData : []).flatMap(details => {
      const locations = getFobLocationObjects(details);
      if (
        isFobIncoterm(details?.incoterm ?? details?.Incoterm) &&
        locations.length
      ) {
        return locations.map(location =>
          toTableRowFromDetail(details, location)
        );
      }
      return [toTableRowFromDetail(details, null)];
    });
  });
};

// Alpha non-LPG disimpan per parcel (QuantityInformationDetail); baris FOB
// per lokasi ditambah incotermLocationId + locationSlot. Alpha null = dikosongkan.
export const updateAlphaHpsRow = async ({
  quantityInformationDetailId,
  incotermLocationId = null,
  locationSlot = null,
  alphaDyedHps = null,
  alphaUndyedHps = null,
}) => {
  const token = getAuthToken();
  if (!token || !quantityInformationDetailId) {
    throw new Error('Missing auth token or parcel id');
  }

  const response = await fetch(
    `${api.API_URL}/quantity-incoterm-location-detail/alpha-hps`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        quantityInformationDetailId,
        incotermLocationId,
        locationSlot,
        alphaDyedHps,
        alphaUndyedHps,
      }),
    }
  );

  if (!response.ok) {
    // status + body dibaca notify.apiError supaya pesan BE tampil di toast.
    const error = new Error(`Failed to update alpha HPS (${response.status})`);
    error.status = response.status;
    error.data = await response.json().catch(() => null);
    throw error;
  }

  const result = await response.json().catch(() => null);
  return result?.data ?? result;
};

export const fetchHpsNonLpgIncotermRows = async ({
  procurementId,
  quantityDeliveryTermId,
}) => {
  const token = getAuthToken();
  if (!token || !procurementId || !quantityDeliveryTermId) return [];

  const response = await fetch(
    `${api.API_URL}/procurement/get-delivery-detail?PageNumber=1&PageSize=100&procurementId=${procurementId}&quantityDeliveryTermId=${quantityDeliveryTermId}`,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) return [];

  const result = await response.json();
  return buildRowsFromDeliveryDetailResult(result);
};
